import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '@/services/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

const PAIN_POINTS = [
  { id: 'missed_calls_slow_replies', label: 'Missed calls / slow replies', icon: '📞' },
  { id: 'no_shows', label: 'Appointment no-shows', icon: '👻' },
  { id: 'weak_follow_up', label: 'Weak follow-up process', icon: '📉' },
  { id: 'lead_routing_ownership', label: 'Lead routing issues', icon: '🔀' },
  { id: 'understaffed_bdc', label: 'Understaffed BDC', icon: '👥' },
  { id: 'inconsistent_messaging', label: 'Inconsistent messaging', icon: '💬' },
  { id: 'poor_appointment_setting', label: 'Poor appointment setting', icon: '📅' },
  { id: 'no_visibility_reporting', label: 'No visibility / reporting', icon: '📊' },
  { id: 'long_response_times', label: 'Long response times', icon: '⏰' },
  { id: 'lost_to_competitors', label: 'Losing deals to competitors', icon: '🏁' },
  { id: 'poor_online_reviews', label: 'Poor online reviews', icon: '⭐' },
  { id: 'manual_processes', label: 'Too many manual processes', icon: '🔧' },
];

const RESPONSE_BUCKETS = [
  { value: '<5m', label: 'Under 5 min' },
  { value: '5-15m', label: '5–15 min' },
  { value: '15-60m', label: '15–60 min' },
  { value: '1-4h', label: '1–4 hours' },
  { value: '4h+', label: '4+ hours' },
];

const ROLES = ['Owner', 'GM', 'Sales Manager', 'BDC', 'Internet Manager'];
const STORE_TYPES = [
  { value: 'dealership', label: 'Dealership', icon: '🏢' },
  { value: 'independent', label: 'Independent', icon: '🏪' },
  { value: 'online_dealer', label: 'Online Dealer', icon: '💻' },
];
/** Form steps after intro (intro = step 0). Results = step 6. */
const FORM_STEP_COUNT = 5;

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function parseOptionalNumber(value) {
  if (value == null) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const cleaned = raw.replace(/,/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function apiErrorToText(err, fallback = 'Failed to calculate. Please try again.') {
  const status = err?.response?.status;
  if (status === 429) {
    return 'Too many attempts from this network. Please wait a few minutes and try again.';
  }
  if (!err?.response && err?.message) {
    return 'Network error. Check your connection and try again.';
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0];
    if (typeof first === 'string') return first;
    if (first?.msg) {
      const loc = Array.isArray(first?.loc) ? first.loc.filter((x) => x !== 'body').join('.') : '';
      return loc ? `${loc}: ${String(first.msg)}` : String(first.msg);
    }
    return 'Some fields are invalid. Please review your inputs and try again.';
  }
  if (detail && typeof detail === 'object') {
    if (detail?.msg) return String(detail.msg);
    return 'Some fields are invalid. Please review your inputs and try again.';
  }
  return fallback;
}

/* text-base (16px) min avoids iOS zooming inputs on focus */
const inputCls =
  'w-full min-h-12 h-12 rounded-xl border border-slate-200 bg-white px-3 text-base text-slate-900 placeholder:text-slate-400 outline-none shadow-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all max-w-full';

const inputClsLarge =
  'w-full min-h-[3.25rem] h-14 rounded-xl border border-slate-200 bg-white px-4 text-base sm:text-lg font-semibold text-slate-900 placeholder:text-slate-400 outline-none shadow-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all tabular-nums max-w-full';

export default function PublicLossCalculatorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trackingRef = (searchParams.get('ref') || '').trim();

  const [wizardStep, setWizardStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverRes, setServerRes] = useState(null);
  const [prefilledFromOutreach, setPrefilledFromOutreach] = useState(false);
  const [outreachMetaLoaded, setOutreachMetaLoaded] = useState(() => !trackingRef);

  const [yourName, setYourName] = useState('');
  const [dealerName, setDealerName] = useState('');
  const [role, setRole] = useState('GM');
  const [storeType, setStoreType] = useState('dealership');

  const [monthlyLeads, setMonthlyLeads] = useState(200);
  const [monthlyAppts, setMonthlyAppts] = useState(60);
  const [showRate, setShowRate] = useState(50);
  const [closeRate, setCloseRate] = useState(20);
  const [avgGrossFront, setAvgGrossFront] = useState('');
  const [avgGrossBack, setAvgGrossBack] = useState('');
  const [respBucket, setRespBucket] = useState('');
  const [painPoints, setPainPoints] = useState([]);

  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');

  useEffect(() => {
    if (!trackingRef) return;
    (async () => {
      try {
        const tr = await api.get(`/audit/track/${encodeURIComponent(trackingRef)}`);
        const mn = (tr.data?.manager_name || '').trim();
        const dn = (tr.data?.dealership_name || '').trim();
        if (mn) setYourName(mn);
        if (dn) setDealerName(dn);
        if (mn && dn) setPrefilledFromOutreach(true);
        await api.post('/public/loss-calculator/start', { tracking_id: trackingRef });
      } catch {
        /* ignore */
      } finally {
        setOutreachMetaLoaded(true);
      }
    })();
  }, [trackingRef]);

  const primary = useMemo(() => {
    const loss = serverRes?.results?.loss || {};
    return {
      low: loss.monthly_loss_low,
      high: loss.monthly_loss_high,
      unitsLost: loss.units_lost_est,
      drivers: loss.drivers || {},
    };
  }, [serverRes]);

  const intakeSummary = useMemo(() => {
    if (!serverRes?.id) return '';
    const r = serverRes?.results || {};
    const inp = r.inputs || {};
    const drv = r.loss?.drivers || {};
    return [
      `Loss Calculator Intake ID: ${serverRes.id}`,
      `Dealer: ${dealerName || '—'} (${storeType})`,
      `Contact: ${yourName || '—'}`,
      `Role: ${role || '—'}`,
      ``,
      `Monthly leads: ${inp.monthly_inbound_leads ?? monthlyLeads}`,
      `Monthly appts set: ${inp.monthly_appts_set ?? monthlyAppts}`,
      `Show rate: ${inp.show_rate_pct ?? showRate}%`,
      `Close rate on shown: ${inp.close_rate_on_shown_pct ?? closeRate}%`,
      `Response time: ${inp.response_time_bucket ?? respBucket}`,
      `Pain points: ${(inp.pain_points || painPoints).join(', ') || '—'}`,
      ``,
      `Estimated monthly loss: $${money(primary.low)} – $${money(primary.high)}`,
      `Units lost estimate: ${primary.unitsLost ?? '—'}`,
      `Drivers (units): speed=${drv.units_lost_to_speed ?? '—'}, no-show=${drv.units_lost_to_no_show ?? '—'}, follow-up=${drv.units_lost_to_followup ?? '—'}`,
    ].join('\n');
  }, [serverRes, dealerName, storeType, yourName, role, monthlyLeads, monthlyAppts, showRate, closeRate, respBucket, painPoints, primary]);

  const togglePain = (id) => {
    setPainPoints((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const frontGross = parseOptionalNumber(avgGrossFront);
      const backGross = parseOptionalNumber(avgGrossBack);
      const combinedGross =
        (frontGross != null || backGross != null)
          ? Number(frontGross || 0) + Number(backGross || 0)
          : null;
      const payload = {
        dealer_name: dealerName,
        role,
        rooftops: '1',
        store_type: storeType,
        monthly_inbound_leads: Number(monthlyLeads) || 0,
        monthly_appts_set: Number(monthlyAppts) || 0,
        // Backward-compatible aliases for older backend deployments.
        monthly_leads: Number(monthlyLeads) || 0,
        monthly_appointments: Number(monthlyAppts) || 0,
        show_rate_pct: Number(showRate) || 0,
        close_rate_on_shown_pct: Number(closeRate) || 0,
        close_rate_pct: Number(closeRate) || 0,
        avg_front_end_gross: frontGross,
        avg_back_end_gross: backGross,
        avg_gross_per_unit: combinedGross,
        response_time_bucket: respBucket,
        pain_points: (painPoints || []).map((p) => String(p)),
        contact_name: yourName || null,
        contact_email: contactEmail || null,
        contact_phone: contactPhone || null,
        tracking_id: trackingRef || null,
      };
      const res = await api.post('/public/loss-calculator/submit', payload);
      setServerRes(res.data);
      goToStep(6);
    } catch (e) {
      alert(apiErrorToText(e));
    } finally {
      setSubmitting(false);
    }
  };

  const goThankYou = () => {
    const raw = (yourName || 'there').trim().split(/\s+/)[0] || 'there';
    const mid =
      primary.low != null && primary.high != null
        ? Math.round((Number(primary.low) + Number(primary.high)) / 2)
        : null;
    navigate('/audit/thank-you', { state: { firstName: raw, amount: mid } });
  };

  const goToStep = useCallback((target) => {
    setDirection(target > wizardStep ? 1 : -1);
    setTransitioning(true);
    setTimeout(() => {
      setWizardStep(target);
      window.scrollTo({ top: 0, behavior: 'instant' });
      setTimeout(() => setTransitioning(false), 20);
    }, 200);
  }, [wizardStep]);

  const next = () => {
    if (wizardStep === 5) {
      submit();
      return;
    }
    goToStep(wizardStep + 1);
  };

  const back = () => {
    if (wizardStep > 0) goToStep(wizardStep - 1);
  };

  const canAdvance = useMemo(() => {
    switch (wizardStep) {
      case 0: return outreachMetaLoaded;
      case 1:
        if (prefilledFromOutreach) return true;
        return yourName.trim().length > 0;
      case 2: return monthlyLeads > 0 && monthlyAppts > 0;
      case 3: return showRate > 0 && closeRate > 0;
      case 4: return respBucket !== '';
      case 5: return painPoints.length >= 3;
      default: return false;
    }
  }, [wizardStep, outreachMetaLoaded, prefilledFromOutreach, yourName, monthlyLeads, monthlyAppts, showRate, closeRate, respBucket, painPoints]);

  const progressPct =
    wizardStep === 0 ? 0 : wizardStep === 6 ? 100 : ((wizardStep - 1) / FORM_STEP_COUNT) * 100;

  if (wizardStep === 6 && serverRes) {
    return <ResultsView {...{ primary, intakeSummary, serverRes, goThankYou, goToStep, yourName }} />;
  }

  return (
    <div
      className="min-h-[100dvh] overflow-x-hidden bg-slate-50 flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="public-loss-calculator-form"
    >
      <div className="w-full h-1 bg-slate-200 shrink-0">
        <div
          className="h-full bg-gradient-to-r from-cyan-500 via-teal-500 to-indigo-600 transition-all duration-500 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>

      <div className="flex-1 flex items-start sm:items-center justify-center px-3 pt-4 pb-6 sm:p-6 md:p-10 w-full min-w-0">
        <div className="w-full max-w-2xl min-w-0">
          <div className="flex justify-center mb-6">
            <img
              src="/logo.svg"
              alt="LotMate"
              className="h-16 sm:h-20 w-auto max-w-[min(90vw,20rem)] object-contain object-center"
            />
          </div>
          {trackingRef && wizardStep >= 1 && wizardStep <= 5 && (
            <p className="text-[11px] text-slate-500 font-mono mb-3 text-center break-all px-1">Ref: {trackingRef}</p>
          )}

          <div
            className={cn(
              'transition-all duration-200 ease-out',
              transitioning
                ? 'opacity-0 translate-y-3'
                : 'opacity-100 translate-y-0'
            )}
            style={{
              transform: transitioning
                ? `translateY(${direction * 12}px)`
                : 'translateY(0)',
            }}
          >
            {wizardStep === 0 && (
              <StepIntro
                yourName={yourName}
                dealerName={dealerName}
                hasTrackingRef={Boolean(trackingRef)}
              />
            )}
            {wizardStep === 1 && (
              <StepWelcome
                prefilledFromOutreach={prefilledFromOutreach}
                {...{ yourName, setYourName, dealerName, setDealerName, role, setRole, storeType, setStoreType }}
              />
            )}
            {wizardStep === 2 && (
              <StepMonthlyNumbers
                {...{ monthlyLeads, setMonthlyLeads, monthlyAppts, setMonthlyAppts }}
              />
            )}
            {wizardStep === 3 && (
              <StepPerformance
                {...{ showRate, setShowRate, closeRate, setCloseRate, avgGrossFront, setAvgGrossFront, avgGrossBack, setAvgGrossBack }}
              />
            )}
            {wizardStep === 4 && (
              <StepResponseSpeed {...{ respBucket, setRespBucket }} />
            )}
            {wizardStep === 5 && (
              <StepPainPoints {...{ painPoints, togglePain }} />
            )}
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between mt-6 sm:mt-8 w-full min-w-0">
            {wizardStep > 0 ? (
              <Button
                variant="ghost"
                className="h-12 w-full sm:w-auto rounded-xl px-6 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 shrink-0"
                onClick={back}
                disabled={submitting}
              >
                Back
              </Button>
            ) : (
              <div className="hidden sm:block sm:min-w-[5rem]" aria-hidden />
            )}

            {wizardStep === 5 ? (
              <Button
                className="h-12 w-full sm:w-auto rounded-xl px-6 sm:px-8 text-sm font-bold bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white shadow-md"
                onClick={submit}
                disabled={!canAdvance || submitting}
              >
                {submitting ? 'Calculating…' : 'Show My Results'}
              </Button>
            ) : (
              <Button
                className="h-12 w-full sm:w-auto rounded-xl px-6 sm:px-8 text-sm font-bold bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white shadow-md inline-flex items-center justify-center gap-2 shrink-0"
                onClick={next}
                disabled={!canAdvance}
              >
                {wizardStep === 0 && !outreachMetaLoaded && trackingRef ? (
                  <>
                    <span className="inline-block h-4 w-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    Loading…
                  </>
                ) : wizardStep === 0 ? (
                  'Get Started'
                ) : (
                  'Next'
                )}
              </Button>
            )}
          </div>

          {wizardStep >= 1 && wizardStep <= 5 && (
            <div className="mt-4 text-center">
              <span className="text-xs text-slate-500">
                Step {wizardStep} of {FORM_STEP_COUNT}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Step Components                                                    */
/* ------------------------------------------------------------------ */

function StepIntro({ yourName, dealerName, hasTrackingRef }) {
  const first = (yourName || '').trim().split(/\s+/)[0];
  const dealer = (dealerName || '').trim();
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-6 md:p-10 space-y-4 sm:space-y-6 text-center">
      <h1 className="text-xl sm:text-2xl md:text-4xl font-bold tracking-tight text-slate-900 leading-snug break-words px-0.5">
        Ready to find out how much{' '}
        {dealer ? (
          <>
            <span className="text-cyan-600">{dealer}</span> is losing
          </>
        ) : (
          <>
            <span className="text-cyan-600">your dealership</span> is losing
          </>
        )}{' '}
        every month?
      </h1>
      <p className="text-slate-600 text-sm md:text-base max-w-lg mx-auto">
        {hasTrackingRef && first
          ? `Hi ${first} — this takes about 90 seconds. No login required.`
          : hasTrackingRef
            ? 'This takes about 90 seconds. No login required.'
            : 'Rough numbers are fine — get a personalized estimate in about 90 seconds. No login required.'}
      </p>
    </div>
  );
}

function StepWelcome({
  prefilledFromOutreach,
  yourName,
  setYourName,
  dealerName,
  setDealerName,
  role,
  setRole,
  storeType,
  setStoreType,
}) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-snug break-words px-0.5">
          {prefilledFromOutreach ? (
            <>
              A few <span className="text-cyan-600">quick details</span>
            </>
          ) : (
            <>
              See what your dealership is{' '}
              <span className="text-cyan-600">losing</span> every month
            </>
          )}
        </h1>
        <p className="text-sm text-slate-600">
          {prefilledFromOutreach
            ? 'We already have your name and store from your invite — confirm your role and store type below.'
            : 'No login required. Takes 90 seconds.'}
        </p>
      </div>

      <div className="space-y-4">
        {prefilledFromOutreach ? (
          <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-1">
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider text-center">
              From your invite
            </div>
            <p className="text-center text-sm text-slate-800 break-words px-1">
              <span className="font-semibold">{yourName || '—'}</span>
              <span className="text-slate-400 mx-2">·</span>
              <span className="font-semibold">{dealerName || '—'}</span>
            </p>
          </div>
        ) : (
          <>
            <Field label="Your name">
              <input
                className={inputClsLarge}
                value={yourName}
                onChange={(e) => setYourName(e.target.value)}
                placeholder="John Smith"
              />
            </Field>
            <Field label="Dealership name (optional)">
              <input
                className={inputCls}
                value={dealerName}
                onChange={(e) => setDealerName(e.target.value)}
                placeholder="e.g., Winnipeg CJDR"
              />
            </Field>
          </>
        )}
        <Field label="Your role">
          <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
            {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Store type">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            {STORE_TYPES.map((st) => {
              const selected = storeType === st.value;
              return (
                <button
                  key={st.value}
                  type="button"
                  onClick={() => setStoreType(st.value)}
                  className={cn(
                    'bg-slate-50 border rounded-xl p-3 sm:p-4 text-center cursor-pointer transition-all duration-150 min-h-[4.5rem] sm:min-h-0 flex flex-col items-center justify-center',
                    selected
                      ? 'border-cyan-500 bg-cyan-50 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300'
                  )}
                >
                  <div className="text-xl sm:text-2xl mb-0.5 sm:mb-1">{st.icon}</div>
                  <div className={cn('text-[11px] sm:text-xs font-medium leading-tight', selected ? 'text-cyan-700' : 'text-slate-500')}>
                    {st.label}
                  </div>
                </button>
              );
            })}
          </div>
        </Field>
      </div>
    </div>
  );
}

function StepMonthlyNumbers({ monthlyLeads, setMonthlyLeads, monthlyAppts, setMonthlyAppts }) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-snug break-words px-0.5">
          How many <span className="text-cyan-600">leads</span> do you get per month?
        </h1>
        <p className="text-sm text-slate-600">
          Rough numbers are fine — we'll validate later.
        </p>
      </div>

      <div className="space-y-4">
        <Field label="Monthly inbound leads">
          <input
            className={inputClsLarge}
            value={monthlyLeads}
            onChange={(e) => setMonthlyLeads(Number(e.target.value) || 0)}
            inputMode="numeric"
          />
        </Field>
        <Field label="Monthly appointments set">
          <input
            className={inputClsLarge}
            value={monthlyAppts}
            onChange={(e) => setMonthlyAppts(Number(e.target.value) || 0)}
            inputMode="numeric"
          />
        </Field>
      </div>
    </div>
  );
}

function StepPerformance({ showRate, setShowRate, closeRate, setCloseRate, avgGrossFront, setAvgGrossFront, avgGrossBack, setAvgGrossBack }) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-snug break-words px-0.5">
          What are your <span className="text-cyan-600">rates</span>?
        </h1>
        <p className="text-sm text-slate-600">
          Percentages help us estimate the leak.
        </p>
      </div>

      <div className="space-y-4">
        <Field label="Show rate %">
          <div className="relative">
            <input
              className={inputClsLarge}
              value={showRate}
              onChange={(e) => setShowRate(Number(e.target.value) || 0)}
              inputMode="numeric"
            />
            <span className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-base sm:text-lg pointer-events-none">%</span>
          </div>
        </Field>
        <Field label="Close rate on shown %">
          <div className="relative">
            <input
              className={inputClsLarge}
              value={closeRate}
              onChange={(e) => setCloseRate(Number(e.target.value) || 0)}
              inputMode="numeric"
            />
            <span className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-base sm:text-lg pointer-events-none">%</span>
          </div>
        </Field>

        <div className="space-y-1.5">
          <div className="text-xs font-semibold text-slate-600">Avg gross per deal (optional)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 pl-1">Front-end gross</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">$</span>
                <input
                  className={cn(inputCls, 'pl-7')}
                  value={avgGrossFront}
                  onChange={(e) => setAvgGrossFront(e.target.value)}
                  placeholder="2,500"
                  inputMode="numeric"
                />
              </div>
            </div>
            <div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1 pl-1">Back-end gross</div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-sm">$</span>
                <input
                  className={cn(inputCls, 'pl-7')}
                  value={avgGrossBack}
                  onChange={(e) => setAvgGrossBack(e.target.value)}
                  placeholder="800"
                  inputMode="numeric"
                />
              </div>
            </div>
          </div>
          <p className="text-[10px] text-slate-500 pl-1">Combined front + back gross is used to estimate dollar losses per unit.</p>
        </div>
      </div>
    </div>
  );
}

function StepResponseSpeed({ respBucket, setRespBucket }) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-snug break-words px-0.5">
          How fast does your team <span className="text-cyan-600">respond</span> to new leads?
        </h1>
        <p className="text-sm text-slate-600">
          Pick the option closest to your average.
        </p>
      </div>

      <div className="space-y-3">
        {RESPONSE_BUCKETS.map((b) => {
          const selected = respBucket === b.value;
          return (
            <button
              key={b.value}
              type="button"
              onClick={() => setRespBucket(b.value)}
              className={cn(
                'w-full min-h-[3.25rem] py-3 px-2 rounded-xl border text-sm font-semibold transition-all duration-150 leading-snug',
                selected
                  ? 'bg-gradient-to-r from-cyan-500 to-indigo-600 text-white border-transparent shadow-md sm:scale-[1.02]'
                  : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50 hover:border-slate-300'
              )}
            >
              {b.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function StepPainPoints({ painPoints, togglePain }) {
  return (
    <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-6 md:p-8 space-y-5 sm:space-y-6">
      <div className="space-y-2 text-center">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-slate-900 leading-snug break-words px-0.5">
          What are your <span className="text-cyan-600">biggest challenges</span>?
        </h1>
        <p className="text-sm text-slate-600">
          Pick at least 3 that apply
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
        {PAIN_POINTS.map((p) => {
          const on = painPoints.includes(p.id);
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => togglePain(p.id)}
              className={cn(
                'flex items-center gap-2 sm:gap-3 bg-slate-50 border rounded-xl p-2.5 sm:p-3 text-left transition-all duration-150 min-w-0',
                on
                  ? 'border-cyan-500 bg-cyan-50 text-cyan-700'
                  : 'border-slate-200 text-slate-700 hover:bg-slate-100 hover:border-slate-300'
              )}
            >
              <span className="text-base sm:text-lg flex-shrink-0">{p.icon}</span>
              <span className="text-xs sm:text-sm font-medium leading-snug">{p.label}</span>
            </button>
          );
        })}
      </div>

      <div className="text-center">
        <Badge className={cn(
          'text-xs px-3 py-1',
          painPoints.length >= 3
            ? 'bg-cyan-50 text-cyan-700 border-cyan-200'
            : 'bg-slate-100 text-slate-500 border-slate-200'
        )}>
          Selected: {painPoints.length}
        </Badge>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Results View                                                       */
/* ------------------------------------------------------------------ */

function ResultsView({ primary, intakeSummary, serverRes, goThankYou, goToStep, yourName }) {
  const d = primary.drivers || {};
  const driverEntries = [
    { label: 'Speed-to-lead', value: Number(d.units_lost_to_speed || 0), color: 'bg-cyan-500' },
    { label: 'No-shows', value: Number(d.units_lost_to_no_show || 0), color: 'bg-indigo-500' },
    { label: 'Follow-up', value: Number(d.units_lost_to_followup || 0), color: 'bg-violet-500' },
  ];
  const maxDriver = Math.max(...driverEntries.map((e) => e.value), 1);
  const biggestDriver = [...driverEntries].sort((a, b) => b.value - a.value)[0];

  const lowNum = Number(primary.low || 0);
  const highNum = Number(primary.high || 0);
  const midLoss = Math.round((lowNum + highNum) / 2);
  const recoveryPct = 0.65;
  const recoveredMonthly = Math.round(midLoss * recoveryPct);
  const recoveredAnnual = recoveredMonthly * 12;
  const unitsLost = Number(primary.unitsLost || 0);
  const unitsRecoverable = Math.round(unitsLost * recoveryPct);

  const BENEFITS = [
    { icon: '⚡', title: 'AI responds in under 60 seconds', desc: 'Every lead gets an instant, personalized reply — 24/7, nights, weekends, holidays.' },
    { icon: '📅', title: 'Smart appointment setting', desc: 'AI books, confirms, and follows up on appointments automatically. Fewer no-shows.' },
    { icon: '🔄', title: 'Automated follow-up sequences', desc: 'No lead falls through the cracks. Multi-touch follow-ups until they buy or say no.' },
    { icon: '📊', title: 'Full visibility & reporting', desc: "See exactly who's performing, what's leaking, and where deals are stuck — in real time." },
    { icon: '🤖', title: 'AI handles your BDC workload', desc: "Stop paying $4K+/mo per BDC rep. One AI handles what 3 reps can't keep up with." },
    { icon: '🏆', title: 'Proven with 200+ dealers', desc: 'Dealers using LotMate recover 40-65% of lost revenue within 90 days of going live.' },
  ];

  return (
    <div
      className="min-h-[100dvh] overflow-x-hidden bg-slate-50 flex flex-col"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="public-loss-calculator-result"
    >
      <div className="w-full h-1 bg-gradient-to-r from-cyan-500 via-teal-500 to-indigo-600 shrink-0" />

      <div className="flex-1 px-3 pt-4 pb-8 sm:p-6 md:p-10 w-full min-w-0">
        <div className="w-full max-w-4xl mx-auto space-y-6 sm:space-y-8 mt-4 sm:mt-6 md:mt-10 min-w-0">

          {/* Header */}
          <div className="text-center space-y-3">
            <div className="flex justify-center mb-2">
              <img
                src="/logo.svg"
                alt="LotMate"
                className="h-16 sm:h-20 w-auto max-w-[min(90vw,20rem)] object-contain object-center"
              />
            </div>
            <Badge className="bg-cyan-50 text-cyan-800 border-cyan-200 text-xs">
              Your estimate is ready
            </Badge>
            {yourName && (
              <p className="text-sm text-slate-600">
                {yourName.trim().split(/\s+/)[0]}, here's what we found
              </p>
            )}
          </div>

          {/* ═══ THE PROBLEM — What you're losing ═══ */}
          <div className="text-center">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">
              You're currently losing
            </div>
            <div className="text-2xl sm:text-4xl md:text-6xl font-extrabold bg-gradient-to-r from-cyan-600 via-teal-600 to-indigo-600 bg-clip-text text-transparent leading-tight break-words px-1">
              ${money(primary.low)} — ${money(primary.high)}
            </div>
            <div className="text-slate-600 text-sm mt-2">estimated profit leaking <span className="text-cyan-600 font-semibold">every single month</span></div>
          </div>

          {/* Two-column: Drivers + Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Loss driver bars */}
            <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-5 space-y-4 min-w-0">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Where you're losing deals</div>
              {driverEntries.map((entry) => (
                <div key={entry.label} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2 text-sm min-w-0">
                    <span className="text-slate-600 min-w-0 shrink">{entry.label}</span>
                    <span className="text-slate-900 font-semibold tabular-nums shrink-0">{entry.value} units</span>
                  </div>
                  <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={cn('h-full rounded-full transition-all duration-700 ease-out', entry.color)}
                      style={{ width: `${(entry.value / maxDriver) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Key metric cards */}
            <div className="grid grid-cols-2 gap-2 sm:gap-3 min-w-0">
              <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-3 sm:p-4 flex flex-col min-w-0">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Units Lost / mo</div>
                <div className="text-2xl sm:text-3xl font-bold text-slate-900 mt-auto tabular-nums">{primary.unitsLost ?? '—'}</div>
              </div>
              <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-3 sm:p-4 flex flex-col min-w-0">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Biggest Driver</div>
                <div className="text-sm sm:text-lg font-bold text-slate-900 mt-auto leading-tight break-words">{biggestDriver?.label || '—'}</div>
              </div>
              <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-3 sm:p-4 flex flex-col min-w-0">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Annual Loss</div>
                <div className="text-lg sm:text-2xl font-bold text-red-400 mt-auto tabular-nums break-all">${money(midLoss * 12)}</div>
              </div>
              <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-3 sm:p-4 flex flex-col min-w-0">
                <div className="text-[10px] text-slate-500 uppercase tracking-wider">Recovery Potential</div>
                <div className="text-lg sm:text-2xl font-bold text-cyan-600 mt-auto">High</div>
              </div>
            </div>
          </div>

          {/* ═══ THE SOLUTION — What Autopilot recovers ═══ */}
          <div className="relative overflow-hidden bg-gradient-to-br from-cyan-50 to-indigo-50 border border-cyan-100 rounded-2xl p-4 sm:p-6 md:p-8">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-200/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="relative min-w-0">
              <div className="text-xs font-semibold text-cyan-700 uppercase tracking-widest mb-1">With LotMate</div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900 leading-snug break-words">
                Recover up to <span className="text-cyan-600">${money(recoveredMonthly)}/mo</span>
              </h2>
              <p className="text-slate-600 text-sm mt-1 leading-relaxed">
                That's <span className="text-slate-900 font-semibold">${money(recoveredAnnual)}/year</span> back in your pocket — and roughly <span className="text-cyan-600 font-semibold">{unitsRecoverable} extra units</span> per month.
              </p>

              {/* Before / After comparison */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-3">Without LotMate</div>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">Monthly loss</span>
                      <span className="text-red-600 font-semibold tabular-nums text-right break-all">${money(midLoss)}</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">Units lost</span>
                      <span className="text-red-600 font-semibold tabular-nums text-right">{unitsLost}/mo</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">Response time</span>
                      <span className="text-red-600 font-semibold text-right">Hours</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">After-hours</span>
                      <span className="text-red-600 font-semibold text-right">No coverage</span>
                    </div>
                  </div>
                </div>
                <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 min-w-0">
                  <div className="text-[10px] text-cyan-700 uppercase tracking-wider mb-3">With LotMate</div>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">Recovered</span>
                      <span className="text-cyan-700 font-semibold tabular-nums text-right break-all">+${money(recoveredMonthly)}</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">Units saved</span>
                      <span className="text-cyan-700 font-semibold tabular-nums text-right">+{unitsRecoverable}/mo</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">Response time</span>
                      <span className="text-cyan-700 font-semibold text-right">&lt;60 sec</span>
                    </div>
                    <div className="grid grid-cols-[1fr_auto] gap-x-2 gap-y-1 items-baseline">
                      <span className="text-slate-600 min-w-0">After-hours</span>
                      <span className="text-cyan-700 font-semibold text-right">24/7 AI</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ═══ WHY AUTOPILOT — Benefits grid ═══ */}
          <div>
            <div className="text-center mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                How <span className="text-cyan-600">LotMate</span> fixes this
              </h2>
              <p className="text-sm text-slate-600 mt-1">Every feature is built to recover what you're losing</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {BENEFITS.map((b, i) => (
                <div key={i} className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 hover:border-cyan-200 transition-colors">
                  <div className="text-2xl mb-2">{b.icon}</div>
                  <div className="text-sm font-semibold text-slate-900">{b.title}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-relaxed">{b.desc}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ═══ ROI snapshot ═══ */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-4 sm:p-5 min-w-0">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-4">Your ROI snapshot</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 text-center">
              <div className="min-w-0 px-0.5">
                <div className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-600 to-indigo-600 bg-clip-text text-transparent break-all">${money(recoveredMonthly)}</div>
                <div className="text-[10px] text-slate-500 mt-1 uppercase">Monthly recovery</div>
              </div>
              <div className="min-w-0 px-0.5">
                <div className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-600 to-indigo-600 bg-clip-text text-transparent break-all">${money(recoveredAnnual)}</div>
                <div className="text-[10px] text-slate-500 mt-1 uppercase">Annual recovery</div>
              </div>
              <div className="min-w-0 px-0.5">
                <div className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-600 to-indigo-600 bg-clip-text text-transparent">+{unitsRecoverable}</div>
                <div className="text-[10px] text-slate-500 mt-1 uppercase">Units / month</div>
              </div>
              <div className="min-w-0 px-0.5">
                <div className="text-lg sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-cyan-600 to-indigo-600 bg-clip-text text-transparent">&lt;90</div>
                <div className="text-[10px] text-slate-500 mt-1 uppercase">Days to results</div>
              </div>
            </div>
          </div>

          {/* ═══ BOOK APPOINTMENT CTA ═══ */}
          <div className="bg-gradient-to-br from-cyan-50 to-indigo-50 border border-cyan-100 rounded-2xl p-6 md:p-8 space-y-5">
            <div className="text-center space-y-2">
              <h3 className="text-xl md:text-2xl font-bold text-slate-900">Ready to stop the leak?</h3>
              <p className="text-sm text-slate-600">
                Book a 15-minute call with our team — we'll walk through your numbers and show you exactly how LotMate recovers them.
              </p>
            </div>

            <div className="flex justify-center pt-2 px-1">
              <Button
                className="h-12 w-full max-w-md rounded-xl px-4 sm:px-8 text-sm font-bold bg-gradient-to-r from-cyan-500 to-indigo-600 hover:from-cyan-400 hover:to-indigo-500 text-white shadow-md whitespace-normal text-center leading-snug"
                type="button"
                onClick={goThankYou}
              >
                Book Appointment with Our Team
              </Button>
            </div>

            <div className="text-center">
              <p className="text-xs text-slate-500">No commitment. We'll show you what LotMate can do with your specific data.</p>
            </div>
          </div>

          {/* What we'll cover */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-2xl p-5">
            <div className="text-sm font-semibold text-slate-900">What we'll cover on the call</div>
            <div className="mt-3 text-sm text-slate-600 space-y-2">
              <div className="flex items-start gap-2">
                <span className="text-cyan-600 mt-0.5">→</span>
                <span>Speed-to-lead + missed reply recovery</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-cyan-600 mt-0.5">→</span>
                <span>Appointment outcomes + manager-approved follow-ups</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-cyan-600 mt-0.5">→</span>
                <span>Visibility: what's working, what's leaking, who needs help</span>
              </div>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="flex flex-wrap gap-2 justify-center">
            <Button
              className="h-9 rounded-xl bg-transparent text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              variant="ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(intakeSummary);
                  alert('Copied intake summary.');
                } catch {
                  alert('Copy failed.');
                }
              }}
            >
              Copy summary
            </Button>
            <Button
              className="h-9 rounded-xl text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              variant="ghost"
              onClick={() => goToStep(1)}
            >
              Edit answers
            </Button>
          </div>

          <div className="text-center text-[11px] text-slate-500">
            Intake ID: <span className="font-mono">{serverRes?.id}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

function Field({ label, children }) {
  return (
    <div className="space-y-1.5 min-w-0">
      <div className="text-xs font-semibold text-slate-600">{label}</div>
      {children}
    </div>
  );
}
